/**
 * HelpAboutPage — concise app + operator card. Pulls only safe metadata:
 *   - app name + product strapline (static)
 *   - version (static — `Version 1.0`, in lockstep with the manuals)
 *   - environment label derived from the current tenant's effective
 *     activation status when available (`Trial`, `Production`, etc.)
 *   - frontend build (NODE_ENV) — never internal hostnames or URLs
 *   - backend availability via `/api/health` — boolean only
 *   - QUOD-Tech Ltd registration block
 *
 * Never renders DB names, env vars, provider readiness, or hostnames.
 */
import React, { useEffect, useState } from 'react';
import AppHeader from '../layout/AppHeader';
import { useFeatures } from '../../context/FeatureContext';

const API = process.env.REACT_APP_BACKEND_URL;

const VERSION = '1.0';

function envLabelFromActivation(status) {
  if (!status) return null;
  switch (status) {
    case 'trial':     return 'Trial';
    case 'active':    return 'Production';
    case 'expired':   return 'Trial expired';
    case 'suspended': return 'Suspended';
    case 'cancelled': return 'Cancelled';
    default:          return null;
  }
}

function Field({ label, value, testid }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0' }}>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600 }} data-testid={testid}>{value || '—'}</span>
    </div>
  );
}

export default function HelpAboutPage() {
  const { activationStatus } = useFeatures();
  const [backendOk, setBackendOk] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/health`, { credentials: 'include' });
        if (!cancelled) setBackendOk(r.ok);
      } catch (_) {
        if (!cancelled) setBackendOk(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const envLabel = envLabelFromActivation(activationStatus);
  const frontendBuild = (process.env.NODE_ENV || 'production').replace(/^./, (c) => c.toUpperCase());
  const backendStatus = backendOk === null ? 'Checking…' : (backendOk ? 'Available' : 'Unavailable');

  return (
    <>
      <AppHeader active="help" />
      <div className="page-shell" data-testid="help-about-page">
        <div className="qf-glass-panel" style={{ padding: 24, marginTop: 12, maxWidth: 720 }}>
          <h2 style={{ marginTop: 0, fontSize: 22, fontWeight: 700 }}>About QUOD-Finder™</h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            Sector-aware opportunity intelligence platform &mdash; built and operated by QUOD-Tech Ltd.
          </p>

          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <Field label="Product"           value="QUOD-Finder™"          testid="about-product" />
            <Field label="Version"           value={`Version ${VERSION}`}  testid="about-version" />
            <Field label="Environment"       value={envLabel || '—'}        testid="about-environment" />
            <Field label="Frontend build"    value={frontendBuild}          testid="about-frontend-build" />
            <Field label="Backend API"       value={backendStatus}          testid="about-backend-status" />
          </div>

          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px' }}>Operated by</h3>
            <Field label="Company"             value="QUOD-Tech Ltd"                                        testid="about-company" />
            <Field label="Website"             value={<a href="https://www.quod-tech.com" target="_blank" rel="noopener noreferrer" data-testid="about-website-link">www.quod-tech.com</a>} testid="about-website" />
            <Field label="Contact"             value={<a href="mailto:info@quod-tech.com?subject=QUOD-Finder%20Support" data-testid="about-contact-link">info@quod-tech.com</a>} testid="about-contact" />
            <Field label="Company number"      value="16984972"                                              testid="about-company-number" />
            <Field label="Registered in"       value="England and Wales"                                     testid="about-jurisdiction" />
            <Field label="Registered office"   value="124 City Road, London, England, EC1V 2NX"             testid="about-office" />
          </div>

          <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 16, marginBottom: 0 }}>
            This page never exposes secrets, environment variables, database names, provider keys, or internal hostnames.
          </p>

          <div
            style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border)' }}
            data-testid="about-ip-notice"
          >
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px' }}>Ownership &amp; notices</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 6px' }}>
              &copy; 2026 QUOD-Tech Ltd. All rights reserved. QUOD-Finder is
              proprietary software owned by QUOD-Tech Ltd.
            </p>
            <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 6px' }}>
              QUOD-Tech, QUOD-Finder and related logos are trade names, product
              names and brand assets of QUOD-Tech Ltd. Trademark registration
              status may vary by jurisdiction.
            </p>
            <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 6px' }}>
              Trial and evaluation access is provided for evaluation only and
              subject to QUOD-Tech Ltd&apos;s evaluation terms. Outputs are
              decision-support material &mdash; review the underlying evidence
              before acting.
            </p>
            <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: 0 }}>
              <a href="/docs/COPYRIGHT.md" target="_blank" rel="noopener noreferrer" data-testid="about-copyright-link">Copyright notice</a>
              {' · '}
              <a href="/docs/EVALUATION_TERMS.md" target="_blank" rel="noopener noreferrer" data-testid="about-eval-terms-link">Evaluation terms (draft)</a>
              {' · '}
              <a href="/docs/TRADEMARKS.md" target="_blank" rel="noopener noreferrer" data-testid="about-trademarks-link">Trademark notice</a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
